// src/pages/Home/LastGallerie.jsx
import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { getAllGalleries } from "../../services/gallerieServices";
import Button from "../../components/Button/Button";
import { useUser } from "../../contexts/UserContext";
import {
  FaChevronLeft,
  FaChevronRight,
  FaTimes,
  FaImages,
} from "react-icons/fa";

const LastGallerie = () => {
  const [galleries, setGalleries] = useState([]);
  const [loading, setLoading] = useState(true);

  const [selectedGallery, setSelectedGallery] = useState(null);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [isZoomed, setIsZoomed] = useState(false);

  const navigate = useNavigate();
  const { user } = useUser();

  useEffect(() => {
    const fetchGalleries = async () => {
      try {
        const allGalleries = await getAllGalleries();
        const activeGalleries = (allGalleries || []).filter(
          (gallery) => gallery.is_active
        );
        // On garde les 3 derniers albums pour la Home
        setGalleries(activeGalleries.slice(0, 3));
      } catch (error) {
        console.error("Failed to fetch galleries", error);
      } finally {
        setLoading(false);
      }
    };

    fetchGalleries();
  }, []);

  const handleReadMore = (galleryId) => {
    navigate(`/galerie-photos/album/${galleryId}`);
  };

  const openGalleryModal = (gallery) => {
    setSelectedGallery(gallery);
    setCurrentPhotoIndex(0);
    setIsZoomed(false);
  };

  const closeGalleryModal = () => {
    setSelectedGallery(null);
    setCurrentPhotoIndex(0);
    setIsZoomed(false);
  };

  const activePhotos = useMemo(() => {
    if (!selectedGallery || !selectedGallery.photos) return [];
    return selectedGallery.photos.filter((p) => p.is_active !== false);
  }, [selectedGallery]);

  const hasPhotos = activePhotos.length > 0;
  const currentPhoto =
    hasPhotos && activePhotos[currentPhotoIndex]
      ? activePhotos[currentPhotoIndex]
      : null;

  const goNextPhoto = () => {
    if (!hasPhotos) return;
    setCurrentPhotoIndex((prev) => (prev + 1) % activePhotos.length);
  };

  const goPrevPhoto = () => {
    if (!hasPhotos) return;
    setCurrentPhotoIndex(
      (prev) => (prev - 1 + activePhotos.length) % activePhotos.length
    );
  };

  const handleCardDescriptionClick = (e) => {
    // Si jamais tu as des liens dans la description, on bloque leur ouverture
    const target = e.target;
    if (target && target.closest("a")) {
      e.preventDefault();
    }
  };

  /* ========== Loading ========== */
  if (loading) {
    return (
      <section className="py-10 md:py-14 bg-slate-50 dark:bg-slate-900">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center text-center mb-6 md:mb-8 animate-pulse">
            <div className="h-6 w-64 bg-slate-200 dark:bg-slate-700 rounded mb-2" />
            <div className="h-4 w-40 bg-slate-200 dark:bg-slate-700 rounded" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="rounded-2xl bg-slate-100 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700 h-64"
              />
            ))}
          </div>
        </div>
      </section>
    );
  }

  /* ========== Pas de galleries ========== */
  if (!galleries.length) {
    return (
      <section className="py-10 md:py-14 bg-slate-50 dark:bg-slate-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs font-semibold tracking-[0.3em] uppercase text-primary mb-1">
            Galeries photos
          </p>
          <h2 className="text-2xl md:text-3xl font-extrabold tracking-tight mb-2">
            Derniers albums photos
          </h2>
          <p className="text-sm md:text-base text-slate-600 dark:text-slate-400">
            Aucune galerie n’est publiée pour le moment.
          </p>
        </div>
      </section>
    );
  }

  /* ========== Affichage normal ========== */
  return (
    <>
      <section className="py-10 md:py-14 bg-slate-50 dark:bg-slate-900">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Titre */}
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-2 mb-6 md:mb-8">
            <div>
              <p className="text-xs font-semibold tracking-[0.3em] uppercase text-primary mb-1">
                Galeries photos
              </p>
              <h2 className="text-2xl md:text-3xl font-extrabold tracking-tight">
                Derniers albums photos
              </h2>
            </div>
            <p className="text-xs md:text-sm text-slate-600 dark:text-slate-400 max-w-md">
              Sélection de clichés issus des compétitions, stages et événements
              du Judo girondin.
            </p>
          </div>

          {/* Cards des albums (3 max) */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            {galleries.map((gallery) => {
              const firstPhoto =
                gallery.photos && gallery.photos.length > 0
                  ? gallery.photos[0].photo_url
                  : "/img/cours_judo.jpg";

              return (
                <article
                  key={gallery.id}
                  onClick={() => openGalleryModal(gallery)}
                  className="group flex flex-col bg-white dark:bg-slate-800/90 border border-slate-200 dark:border-slate-700 rounded-2xl overflow-hidden shadow-md hover:shadow-2xl hover:-translate-y-1 transition-all duration-200 cursor-pointer"
                >
                  {/* Image de couverture */}
                  <div className="relative h-44 md:h-48 overflow-hidden">
                    <img
                      src={firstPhoto}
                      alt={gallery.titre}
                      className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/25 to-transparent pointer-events-none" />

                    {/* Badge nb de photos */}
                    {gallery.photos && gallery.photos.length > 0 && (
                      <div className="absolute left-3 bottom-3 bg-white/95 rounded-full px-3 py-1 text-[11px] font-semibold text-slate-900 shadow-md inline-flex items-center gap-2">
                        <FaImages className="text-primary" />
                        <span>{gallery.photos.length} photo(s)</span>
                      </div>
                    )}
                  </div>

                  {/* Contenu */}
                  <div className="flex flex-col flex-grow p-4 space-y-3">
                    <h3 className="text-base md:text-lg font-semibold text-slate-900 dark:text-slate-50">
                      {gallery.titre}
                    </h3>
                    {gallery.description && (
                      <div
                        className="text-xs md:text-sm text-slate-700 dark:text-slate-300 line-clamp-5"
                        onClick={handleCardDescriptionClick}
                        dangerouslySetInnerHTML={{
                          __html: gallery.description,
                        }}
                      />
                    )}
                  </div>

                  {/* Bouton Voir album (route dédiée) – réservé aux utilisateurs connectés */}
                  {user && (
                    <div
                      className="px-4 pb-4"
                      onClick={(e) => e.stopPropagation()} // ne pas rouvrir la modale
                    >
                      <Button
                        text="Voir l'album complet"
                        onClick={() => handleReadMore(gallery.id)}
                        className="mt-1 gap-2 w-full justify-center"
                      />
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* MODALE GALLERIE – viewer complet */}
      {selectedGallery && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-3 sm:px-6">
          {/* Fond sombre */}
          <div
            className="absolute inset-0 bg-slate-900/80 backdrop-blur-sm"
            onClick={closeGalleryModal}
          />

          {/* Contenu modale */}
          <div
            className="relative max-w-5xl w-full max-h-[90vh] bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Bouton fermer */}
            <button
              type="button"
              onClick={closeGalleryModal}
              className="absolute top-3 right-3 z-10 inline-flex items-center justify-center w-8 h-8 rounded-full bg-slate-900/80 text-slate-100 hover:bg-slate-800"
              aria-label="Fermer"
            >
              <FaTimes className="w-4 h-4" />
            </button>

            {/* En-tête */}
            <div className="px-4 sm:px-6 pt-4 pb-3 border-b border-slate-200 dark:border-slate-700 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div>
                <p className="text-[11px] font-semibold tracking-[0.3em] uppercase text-primary mb-1">
                  Album photos
                </p>
                <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-slate-900 dark:text-slate-50">
                  {selectedGallery.titre}
                </h3>
              </div>
              <div className="text-[11px] sm:text-xs text-slate-600 dark:text-slate-300 flex items-center gap-2">
                <FaImages />
                <span>
                  {hasPhotos ? `${currentPhotoIndex + 1}/${activePhotos.length}` : "Aucune photo"}
                </span>
              </div>
            </div>

            {/* Viewer principal */}
            <div className="flex-1 flex flex-col">
              {/* Zone image principale */}
              <div
                className={`relative flex-1 bg-black flex items-center justify-center ${
                  isZoomed ? "cursor-zoom-out" : "cursor-zoom-in"
                }`}
                onClick={() => hasPhotos && setIsZoomed((z) => !z)}
              >
                {currentPhoto ? (
                  <img
                    src={currentPhoto.photo_url}
                    alt={currentPhoto.description || selectedGallery.titre}
                    className={`transition-all duration-300 ${
                      isZoomed
                        ? "max-h-[80vh] max-w-full object-contain"
                        : "h-full w-full object-contain md:object-cover"
                    }`}
                  />
                ) : (
                  <div className="text-slate-200 text-sm">
                    Aucune photo disponible pour cet album.
                  </div>
                )}

                {/* Flèches navigation photo */}
                {hasPhotos && activePhotos.length > 1 && (
                  <>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        goPrevPhoto();
                      }}
                      className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-slate-900/80 text-white flex items-center justify-center hover:bg-slate-800"
                    >
                      <FaChevronLeft className="text-lg" />
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        goNextPhoto();
                      }}
                      className="absolute right-3 sm:right-4 top-1/2 -translate-y-1/2 w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-slate-900/80 text-white flex items-center justify-center hover:bg-slate-800"
                    >
                      <FaChevronRight className="text-lg" />
                    </button>
                  </>
                )}

                {/* Info zoom */}
                {hasPhotos && (
                  <div className="absolute bottom-3 right-3 bg-black/70 text-white text-[11px] px-3 py-1 rounded-full">
                    Cliquer pour {isZoomed ? "réduire" : "agrandir"}
                  </div>
                )}
              </div>

              {/* Bandeau description + miniatures */}
              <div className="border-t border-slate-200 dark:border-slate-700 bg-slate-50/90 dark:bg-slate-900/95">
                {/* Description de la photo courante (si existante) */}
                {currentPhoto?.description && (
                  <div className="px-4 sm:px-6 py-2 text-xs sm:text-sm text-slate-700 dark:text-slate-300">
                    {currentPhoto.description}
                  </div>
                )}

                {/* Thumbnails */}
                {hasPhotos && (
                  <div className="px-3 sm:px-4 py-2">
                    <div className="flex gap-2 overflow-x-auto pb-1">
                      {activePhotos.map((photo, idx) => (
                        <button
                          key={photo.id || idx}
                          type="button"
                          onClick={() => {
                            setCurrentPhotoIndex(idx);
                            setIsZoomed(false);
                          }}
                          className={`relative flex-shrink-0 w-16 h-16 sm:w-20 sm:h-20 border rounded-md overflow-hidden ${
                            idx === currentPhotoIndex
                              ? "border-primary ring-2 ring-primary/60"
                              : "border-slate-300 dark:border-slate-700"
                          }`}
                        >
                          <img
                            src={photo.photo_url}
                            alt={photo.description || selectedGallery.titre}
                            className="w-full h-full object-cover"
                          />
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Bouton vers la page Album (optionnel, accessible même depuis la modale) */}
                {user && (
                  <div className="px-4 sm:px-6 pb-3 pt-1">
                    <Button
                      text="Ouvrir l'album sur la page dédiée"
                      onClick={() => {
                        closeGalleryModal();
                        handleReadMore(selectedGallery.id);
                      }}
                      className="w-full justify-center mt-1"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default LastGallerie;
